import {
  useActiveGithubOrgForUserNonSuspense,
  useIsImpersonatingNonSuspense,
} from "@/hooks/useConnectedGithubOrgs";
import { useCodesmithLeftSidebarRedesignEnabledForOrg } from "@/hooks/useCodesmithSettings";
import { useCachedOrgFlag } from "./useCachedOrgFlag";
import {
  SIDEBAR_REDESIGN_STORAGE_KEY_PREFIX,
  type CodesmithSidebarVariant,
} from "./sidebarVariant";
import type { NamedOrg } from "@/lib/named-org";

export function useCachedSidebarRedesignEnabled(args: {
  orgName: string | null;
  enabled: boolean;
  enabledKnown: boolean;
}): boolean {
  return useCachedOrgFlag(SIDEBAR_REDESIGN_STORAGE_KEY_PREFIX, args);
}

/**
 * Resolves the sidebar variant for `orgName`: a viewer's own orgs read the
 * connected-installation flag; an impersonated org (absent from the viewer's
 * installations) reads codesmith-settings. Unknown resolves to the cached
 * value for the org, then `legacy`.
 */
export function useCodesmithSidebarVariant(
  org: NamedOrg | null,
): CodesmithSidebarVariant {
  const isImpersonating = useIsImpersonatingNonSuspense();
  const { codesmithLeftSidebarRedesignEnabled, codesmithEnabledKnown } =
    useActiveGithubOrgForUserNonSuspense();
  const impersonated = useCodesmithLeftSidebarRedesignEnabledForOrg(
    isImpersonating ? org : null,
  );
  const enabled = useCachedSidebarRedesignEnabled({
    orgName: org?.orgName ?? null,
    enabled: isImpersonating
      ? impersonated.enabled
      : codesmithLeftSidebarRedesignEnabled,
    enabledKnown: isImpersonating
      ? impersonated.enabledKnown
      : codesmithEnabledKnown,
  });
  return enabled ? "redesign" : "legacy";
}
